import React, { useState, useEffect } from 'react';
import { MapPin } from 'lucide-react';
import PageShell from '../common/PageShell.jsx';
import PageHero from '../common/PageHero.jsx';
import CopyButton from '../common/CopyButton.jsx';

const MAPS_URL = 'https://maps.google.com/?q=MIRA+RD+EAST,+THANE,+MAHARASHTRA+INDIA,+401107';

const Contact = () => {
  useEffect(() => {
    document.title = 'Contact Us | Eco World Capital';
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    description: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (submitted) setSubmitted(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const whatsappNumber = '919321712863';
    const message = `*New Website Enquiry*%0A%0A*Name:* ${formData.name}%0A*Mobile:* ${formData.mobile}%0A*Email:* ${formData.email}%0A*Message:* ${formData.description}`;
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
    setFormData({ name: '', mobile: '', email: '', description: '' });
    setSubmitted(true);
  };

  const inputClass =
    'w-full bg-black/10 border border-brand-cream/30 rounded-xl px-4 py-3 text-brand-cream placeholder-brand-cream/50 focus:outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/50 transition-colors';

  return (
    <PageShell>
      <div className="flex w-full max-w-full flex-col min-h-screen overflow-x-hidden">
        <PageHero
          badge="Get In Touch"
          title="Contact Us"
          subtitle="Have a question or ready to partner with us? Fill out the form below or use our official banking details for secure transactions."
        />

        <section className="pt-4 pb-24 px-4 sm:px-6 md:px-12 max-w-6xl mx-auto flex-grow w-full min-w-0">
          <div className="grid lg:grid-cols-2 gap-12 items-start min-w-0">
            <div className="space-y-8 min-w-0">
              <div className="glass-card p-6 sm:p-8 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-brand-cream/20 to-transparent pointer-events-none" aria-hidden="true" />
                <div className="relative z-10 flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-brand-gold to-brand-gold-light rounded-xl flex items-center justify-center shrink-0 shadow-inner text-brand-primary">
                    <MapPin size={22} aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-xl font-extrabold text-brand-gold tracking-wide mb-2">Corporate Office</h3>
                    <p className="text-base text-brand-cream leading-relaxed font-medium">
                    MIRA RD EAST
                      <br />
                      THANE, MAHARASHTRA INDIA
                      <br />
                      401107
                    </p>
                    <a
                      href={MAPS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-3 text-sm font-bold text-brand-gold-light hover:text-brand-cream transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded"
                    >
                      Open in Google Maps →
                    </a>
                  </div>
                </div>
              </div>

              <div className="glass-card p-6 sm:p-8 md:p-10 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-brand-cream/20 to-transparent pointer-events-none" aria-hidden="true" />
                <h3 className="text-2xl font-extrabold text-brand-cream mb-6 relative z-10">Send an Enquiry</h3>

                {submitted && (
                  <p className="mb-5 p-4 rounded-xl bg-brand-gold/20 border border-brand-gold/40 text-brand-cream font-semibold text-sm relative z-10" role="status">
                    Your enquiry was opened in WhatsApp. If it did not open, please contact us at +91 9321712863.
                  </p>
                )}

                <form onSubmit={handleSubmit} className="relative z-10 space-y-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-bold text-brand-gold-light mb-2">
                      Full Name
                    </label>
                    <input id="contact-name" type="text" name="name" required value={formData.name} onChange={handleChange} placeholder="Enter your name" className={inputClass} />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="contact-mobile" className="block text-sm font-bold text-brand-gold-light mb-2">
                        Mobile Number
                      </label>
                      <input id="contact-mobile" type="tel" name="mobile" required pattern="[0-9+\s-]{10,15}" value={formData.mobile} onChange={handleChange} placeholder="+91 98765 43210" className={inputClass} />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-bold text-brand-gold-light mb-2">
                        Email Address
                      </label>
                      <input id="contact-email" type="email" name="email" required value={formData.email} onChange={handleChange} placeholder="you@example.com" className={inputClass} />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-description" className="block text-sm font-bold text-brand-gold-light mb-2">
                      Description
                    </label>
                    <textarea id="contact-description" name="description" required value={formData.description} onChange={handleChange} rows="4" placeholder="How can we help you?" className={`${inputClass} resize-none`} />
                  </div>

                  <button type="submit" className="w-full btn-primary text-lg px-8 py-4 rounded-xl flex items-center justify-center gap-3">
                    Submit via WhatsApp
                  </button>
                </form>
              </div>
            </div>

            <div className="space-y-8 min-w-0">
              <div className="glass-card p-6 sm:p-8 md:p-10 shimmer-hover overflow-hidden">
                <div className="relative z-10 min-w-0">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-extrabold text-brand-gold mb-4 leading-snug break-words">
                    Bank Details for IMPS/ RTGS/NEFT/UPI/GPAY/PHONEPE/QR CODE.
                  </h3>
                  <p className="text-sm text-brand-cream-muted font-medium mb-6 p-3 rounded-lg bg-black/20 border border-white/10">
                    Please verify account details before transferring funds. EWC is not responsible for transfers sent to incorrect accounts.
                  </p>

                  <div className="space-y-6">
                    <div className="border-l-4 border-brand-gold pl-4">
                      <p className="text-xs text-brand-gold-light font-bold uppercase tracking-wider mb-1">Bank Name</p>
                      <p className="text-xl font-extrabold text-brand-cream">SLICE SMALL FINANCE BANK</p>
                    </div>

                    <div className="border-l-4 border-brand-gold pl-4">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-xs text-brand-gold-light font-bold uppercase tracking-wider">Current A/C No.</p>
                        <CopyButton value="033311501027026" label="Copy" />
                      </div>
                      <p className="text-lg sm:text-2xl font-extrabold text-brand-cream tracking-wide sm:tracking-widest break-all">033311501027026</p>
                    </div>

                    <div className="border-l-4 border-brand-gold pl-4">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-xs text-brand-gold-light font-bold uppercase tracking-wider">IFSC</p>
                        <CopyButton value="NESF0000333" label="Copy" />
                      </div>
                      <p className="text-lg sm:text-xl font-extrabold text-brand-cream tracking-wide sm:tracking-widest break-all">NESF0000333</p>
                    </div>

                    <div className="border-l-4 border-brand-gold pl-4">
                      <p className="text-xs text-brand-gold-light font-bold uppercase tracking-wider mb-1">Bank Address</p>
                      <p className="text-base font-extrabold text-brand-cream leading-relaxed">
                        ASHFORD PARK, Bengaluru, Karnataka, 560034
                      </p>
                    </div>

                    <div className="bg-black/20 p-4 rounded-xl border border-white/10 flex items-center justify-between gap-4 mt-6 flex-wrap">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shrink-0">
                          <span className="text-xl font-black text-blue-600">G</span>
                          <span className="text-xl font-black text-green-600">Pay</span>
                        </div>
                        <div>
                          <p className="text-xs text-brand-gold-light font-bold uppercase tracking-wider">GPay</p>
                          <p className="text-lg sm:text-2xl font-extrabold text-brand-cream tracking-wide sm:tracking-widest break-all">9321712863</p>
                        </div>
                      </div>
                      <CopyButton value="9321712863" label="Copy" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="glass-card p-3 relative overflow-hidden h-[300px]">
                <iframe
                  title="Eco World Capital office location map"
                  className="w-full h-full rounded-2xl"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d21615.29726392122!2d72.87653655!3d19.28216595!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b0458cf7298b%3A0x468ed839e9df2b21!2sMira%20Road%20East%2C%20Mira%20Bhayandar%2C%20Maharashtra!5e1!3m2!1sen!2sin!4v1781596760777!5m2!1sen!2sin"
                  loading="lazy"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </PageShell>
  );
};

export default Contact;
